/**
 * Types for the Sheets canvas grid component
 */

export interface GridTheme {
  bg: number;
  bgSecondary: number;
  border: number;
  text: number;
  textMuted: number;
  accent: number;
  header: number;
}

export const themes: Record<'dark' | 'light', GridTheme> = {
  dark: {
    bg: 0x09090b,        // zinc-950
    bgSecondary: 0x18181b, // zinc-900
    border: 0x27272a,    // zinc-800
    text: 0xfafafa,      // zinc-50
    textMuted: 0x71717a, // zinc-500
    accent: 0x3b82f6,    // blue-500
    header: 0xa1a1aa,    // zinc-400
  },
  light: {
    bg: 0xfafafa,        // zinc-50
    bgSecondary: 0xf4f4f5, // zinc-100
    border: 0xe4e4e7,    // zinc-200
    text: 0x18181b,      // zinc-900
    textMuted: 0x71717a, // zinc-500
    accent: 0x3b82f6,    // blue-500
    header: 0x52525b,    // zinc-600
  },
};

// Grid dimensions
export const CELL_WIDTH = 140;
export const CELL_HEIGHT = 24;
export const HEADER_HEIGHT = 28;
export const SCROLLBAR_WIDTH = 8;

// Data loading
// Note: Lambda responses are limited to 6MB. Wide files need smaller chunks.
export const DEFAULT_CHUNK_SIZE = 3000;
export const EDGE_PREFETCH_THRESHOLD = 1500; // Prefetch when within 1500 rows of unloaded data
export const FETCH_DEBOUNCE_MS = 30; // Fast response for scrolling
export const MAX_FETCH_RETRIES = 3; // Stop retrying after 3 failures

// Viewport-first loading for wide files (>20 columns)
export const VIEWPORT_INITIAL_ROWS = 100;   // rows for instant first paint
export const PREFETCH_EXTRA_ROWS = 1000;     // prefetch rows beyond viewport
export const PREFETCH_EXTRA_COLS = 30;       // prefetch columns beyond viewport

/** Calculate chunk size based on column count to stay under Lambda 6MB limit */
export function getChunkSize(columnCount: number): number {
  // Rough estimate: ~50 bytes per cell average
  // Target: <5MB response (leave headroom below 6MB limit)
  // 5MB / 50 bytes = 100,000 cells max
  const maxCells = 100_000;
  const size = Math.floor(maxCells / Math.max(columnCount, 1));
  // Clamp between 100 and 3000 (100 min supports files with 297+ columns)
  return Math.max(100, Math.min(DEFAULT_CHUNK_SIZE, size));
}

// Re-export for backward compat (used in some places)
export const CHUNK_SIZE = DEFAULT_CHUNK_SIZE;

// Dynamic row number width based on total rows
export function getRowNumberWidth(totalRows: number): number {
  const digits = totalRows > 0 ? Math.floor(Math.log10(totalRows)) + 1 : 1;
  return Math.max(50, digits * 8 + 20);
}

// Shimmer widths for loading placeholders
export const SHIMMER_WIDTHS = [80, 60, 100, 45, 70, 90, 55, 75];

export interface ColumnFilter {
  selected: Set<string>;
  allValues: string[];
}

export interface GridState {
  scrollX: number;
  scrollY: number;
  sortColumn: string | null;
  sortDirection: 'asc' | 'desc';
  searchColumn: string;
  searchTerm: string;
  hoveredCell: { row: number; col: number } | null;
}
